/**
 * Environment Variable Validation
 *
 * Validates that all required environment variables are present at runtime.
 * This provides clear error messages if the build is misconfigured.
 *
 * IMPORTANT:
 * - Web: Webpack DefinePlugin replaces process.env.REACT_APP_* at build time
 * - iOS/Android: react-native-config loads from .env files at build time
 */

import {
  REACT_APP_API_BASE_URL,
  REACT_APP_API_BASE_URL_WEB,
  REACT_APP_API_BASE_URL_ANDROID,
  REACT_APP_API_BASE_URL_IOS,
  REACT_APP_BASE_URL,
  REACT_APP_API_TOKEN,
  REACT_APP_OKTA_DOMAIN,
  REACT_APP_OKTA_CLIENT_ID,
  REACT_APP_OKTA_REDIRECT_URI,
  REACT_APP_OKTA_REDIRECT_URI_IOS,
  REACT_APP_OKTA_LOGOUT_REDIRECT_URI,
  REACT_APP_OKTA_LOGOUT_REDIRECT_URI_IOS,
  REACT_APP_REFETCH_INTERVAL_MINUTES,
} from '@env';
import { Platform } from 'react-native';
// Use react-native-dotenv (@env) for native platforms - more reliable than react-native-config

// Platform-specific required environment variables
// Web only needs web URL, iOS only needs iOS URL, Android only needs Android URL
const getRequiredEnvVars = (platform: string): string[] => {
  const common = ['REACT_APP_API_BASE_URL', 'REACT_APP_BASE_URL'];

  switch (platform) {
    case 'web':
      return [...common, 'REACT_APP_API_BASE_URL_WEB'];
    case 'ios':
      return [...common, 'REACT_APP_API_BASE_URL_IOS'];
    case 'android':
      return [...common, 'REACT_APP_API_BASE_URL_ANDROID'];
    default:
      return common;
  }
};

export function validateEnvironment(): void {
  const platform = Platform.OS;
  const requiredEnvVars = getRequiredEnvVars(platform);

  // Get environment values based on platform
  const envValues: Record<string, string | undefined> =
    platform === 'web'
      ? {
          // Web uses process.env (webpack DefinePlugin)
          REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
          REACT_APP_API_BASE_URL_WEB: process.env.REACT_APP_API_BASE_URL_WEB,
          REACT_APP_BASE_URL: process.env.REACT_APP_BASE_URL,
        }
      : {
          // iOS/Android use react-native-dotenv (@env)
          REACT_APP_API_BASE_URL,
          REACT_APP_API_BASE_URL_WEB,
          REACT_APP_API_BASE_URL_ANDROID,
          REACT_APP_API_BASE_URL_IOS,
          REACT_APP_BASE_URL,
        };

  const missing: string[] = [];
  const empty: string[] = [];

  for (const varName of requiredEnvVars) {
    const value = envValues[varName];

    if (value === undefined) {
      missing.push(varName);
    } else if (value.trim() === '') {
      empty.push(varName);
    }
  }

  if (missing.length > 0 || empty.length > 0) {
    console.warn('⚠️ ENVIRONMENT CONFIGURATION WARNING\n');
    console.warn(`Platform: ${Platform.OS}\n`);

    if (missing.length > 0) {
      console.warn('Missing required environment variables:');
      missing.forEach(varName => console.warn(`  - ${varName}`));
      console.warn('');
    }

    if (empty.length > 0) {
      console.warn('Empty environment variables (have value but are blank):');
      empty.forEach(varName => console.warn(`  - ${varName}`));
      console.warn('');
    }

    // Don't throw - let the app continue so we can see the real error
    // The app may still work if the missing vars aren't critical for the current flow
    console.warn(
      '⚠️ Continuing despite missing env vars - app may not function correctly',
    );
    return;
  }

  console.log('✅ Environment variables validated successfully');
}

/**
 * Sentry environment detection based on API URL
 */
export type SentryEnvironment = 'development' | 'qa' | 'secure' | 'production';

export function getSentryEnvironment(): SentryEnvironment {
  const apiUrl =
    Platform.OS === 'web'
      ? process.env.REACT_APP_API_BASE_URL || ''
      : REACT_APP_API_BASE_URL || '';

  if (apiUrl.includes('itsapidev') || apiUrl.includes('itsdev'))
    return 'development';
  if (apiUrl.includes('itsapiqa') || apiUrl.includes('itsqa')) return 'qa';
  if (apiUrl.includes('itssecure') || apiUrl.includes('itsapisecure'))
    return 'secure';
  if (apiUrl.includes('itsprod') || apiUrl.includes('its.vertiv'))
    return 'production';

  return 'development';
}

/**
 * Check if Sentry should upload for this detected environment.
 * Production stays off. Secure/QA must upload or TestFlight crashes are invisible.
 */
export function isSentryEnabledFor(env: SentryEnvironment): boolean {
  return env === 'development' || env === 'qa' || env === 'secure';
}

/**
 * Check if Sentry should be enabled for the current API URL.
 */
export function isSentryEnabled(): boolean {
  return isSentryEnabledFor(getSentryEnvironment());
}

/** Privacy-safe Sentry client flags. Session replay and PII stay development-only. */
export function getSentryClientOptions(env: SentryEnvironment): {
  sendDefaultPii: boolean;
  enableLogs: boolean;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
} {
  const isDev = env === 'development';
  return {
    sendDefaultPii: isDev,
    enableLogs: isDev,
    replaysSessionSampleRate: isDev ? 0.1 : 0,
    replaysOnErrorSampleRate: isDev ? 1 : 0,
  };
}

/**
 * Get all REACT_APP environment variables (for debugging)
 */
export function getEnvironmentInfo(): Record<string, string | undefined> {
  const envVars: Record<string, string | undefined> =
    Platform.OS === 'web'
      ? {
          // Web uses process.env (webpack DefinePlugin)
          REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
          REACT_APP_API_BASE_URL_WEB: process.env.REACT_APP_API_BASE_URL_WEB,
          REACT_APP_API_BASE_URL_ANDROID:
            process.env.REACT_APP_API_BASE_URL_ANDROID,
          REACT_APP_API_BASE_URL_IOS: process.env.REACT_APP_API_BASE_URL_IOS,
          REACT_APP_BASE_URL: process.env.REACT_APP_BASE_URL,
          REACT_APP_API_TOKEN: process.env.REACT_APP_API_TOKEN
            ? '***REDACTED***'
            : undefined,
          REACT_APP_OKTA_DOMAIN: process.env.REACT_APP_OKTA_DOMAIN,
          REACT_APP_OKTA_CLIENT_ID: process.env.REACT_APP_OKTA_CLIENT_ID,
          REACT_APP_OKTA_REDIRECT_URI: process.env.REACT_APP_OKTA_REDIRECT_URI,
          REACT_APP_OKTA_REDIRECT_URI_IOS:
            process.env.REACT_APP_OKTA_REDIRECT_URI_IOS,
          REACT_APP_OKTA_LOGOUT_REDIRECT_URI:
            process.env.REACT_APP_OKTA_LOGOUT_REDIRECT_URI,
          REACT_APP_OKTA_LOGOUT_REDIRECT_URI_IOS:
            process.env.REACT_APP_OKTA_LOGOUT_REDIRECT_URI_IOS,
          REACT_APP_REFETCH_INTERVAL_MINUTES:
            process.env.REACT_APP_REFETCH_INTERVAL_MINUTES,
        }
      : {
          // iOS/Android use react-native-dotenv (@env)
          REACT_APP_API_BASE_URL,
          REACT_APP_API_BASE_URL_WEB,
          REACT_APP_API_BASE_URL_ANDROID,
          REACT_APP_API_BASE_URL_IOS,
          REACT_APP_BASE_URL,
          REACT_APP_API_TOKEN: REACT_APP_API_TOKEN
            ? '***REDACTED***'
            : undefined,
          REACT_APP_OKTA_DOMAIN,
          REACT_APP_OKTA_CLIENT_ID,
          REACT_APP_OKTA_REDIRECT_URI,
          REACT_APP_OKTA_REDIRECT_URI_IOS,
          REACT_APP_OKTA_LOGOUT_REDIRECT_URI,
          REACT_APP_OKTA_LOGOUT_REDIRECT_URI_IOS,
          REACT_APP_REFETCH_INTERVAL_MINUTES,
        };

  return envVars;
}
